/**
 * Browser tracing via Logfire.
 *
 * Telemetry is sent to the API server's /client-traces proxy, which forwards
 * it to Logfire with the server-side token — no Logfire token is ever bundled
 * into the client. Fetch/XHR spans carry `traceparent`, so a page action, its
 * GraphQL request, and the server/DB spans behind it land in one trace.
 */
import * as logfire from '@pydantic/logfire-browser';
import { getUsernameFromToken } from './utils/auth';

export const apiBaseUrl = (
  import.meta.env.VITE_GRAPHQL_URI ||
  (import.meta.env.MODE === 'production' ? '/api' : 'http://localhost:4000/')
).replace(/\/$/, '');

const proxyBase = `${apiBaseUrl}/client-traces/v1`;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Cross-origin URLs that may receive trace headers (same-origin always does).
// The API server's CORS config allows these headers.
const propagateTo = [
  new RegExp(`^${escapeRegExp(new URL(apiBaseUrl, window.location.href).origin)}`),
];

export const initTelemetry = () => {
  if (import.meta.env.MODE === 'test') return;

  logfire.configure({
    traceUrl: `${proxyBase}/traces`,
    serviceName: 'ds-stats-client',
    serviceVersion: import.meta.env.VITE_APP_VERSION,
    environment: import.meta.env.MODE,
    metrics: { metricUrl: `${proxyBase}/metrics` },
    autoInstrumentations: {
      '@opentelemetry/instrumentation-fetch': {
        propagateTraceHeaderCorsUrls: propagateTo,
        ignoreUrls: [/\/client-traces\//, /api\.mapbox\.com/, /tiles\.mapbox/],
      },
      '@opentelemetry/instrumentation-xml-http-request': {
        propagateTraceHeaderCorsUrls: propagateTo,
        ignoreUrls: [/\/client-traces\//, /mapbox/],
      },
    },
    resourceTiming: { detail: 'summary' },
    captureGlobalErrors: true,
    rum: {
      session: {
        getUser: () => {
          const username = getUsernameFromToken();
          return username ? { id: username, name: username } : undefined;
        },
      },
      webVitals: true,
    },
  });
};

export { logfire };
